import { useEffect, useState } from 'react';
import { useWalletConnectStore } from '@/stores/walletConnect';
import {
  initWalletConnect,
  pairUri,
  getWalletKit,
} from '@/lib/walletConnect';

export default function Connections() {
  const connectedDapps = useWalletConnectStore((s) => s.connectedDapps);
  const storeError = useWalletConnectStore((s) => s.error);
  const removeDapp = useWalletConnectStore((s) => s.removeDapp);

  const [uriInput, setUriInput] = useState('');
  const [pairing, setPairing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void initWalletConnect();
  }, []);

  async function pair() {
    if (!uriInput.trim()) return;
    setPairing(true);
    setError(null);
    try {
      await pairUri(uriInput.trim());
      setUriInput('');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setPairing(false);
    }
  }

  async function disconnect(topic: string) {
    const kit = getWalletKit();
    if (!kit) return;
    await kit.disconnectSession({
      topic,
      reason: { code: 6000, message: 'User disconnected' },
    });
    removeDapp(topic);
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="font-display text-2xl font-semibold">Connections</h1>
        <p className="text-sm text-ink-300 mt-1">
          Pair an external dApp via WalletConnect URI. Every transaction the
          dApp sends will be queued for your approval.
        </p>
      </div>

      <div className="rounded-xl border border-ink-800 bg-ink-900/40 p-5 space-y-3">
        <h2 className="font-semibold text-ink-100">Pair a new dApp</h2>
        <input
          value={uriInput}
          onChange={(e) => setUriInput(e.target.value)}
          type="text"
          placeholder="wc:..."
          className="w-full rounded-lg border border-ink-800 bg-ink-900 px-4 py-2.5 text-sm font-mono text-ink-50 focus:border-avo-500 focus:outline-none"
        />
        {error && <p className="text-sm text-red-400">{error}</p>}
        {storeError && <p className="text-sm text-amber-400">{storeError}</p>}
        <button
          type="button"
          onClick={() => void pair()}
          disabled={pairing || !uriInput.trim()}
          className="rounded-lg bg-avo-500 px-4 py-2 text-sm font-medium text-ink-950 hover:bg-avo-400 transition-colors disabled:opacity-50"
        >
          {pairing ? 'Pairing…' : 'Pair'}
        </button>
      </div>

      <div>
        <h2 className="text-sm font-semibold text-ink-200 mb-2">
          Connected dApps ({connectedDapps.length})
        </h2>
        {connectedDapps.length === 0 ? (
          <div className="rounded-xl border border-ink-800 bg-ink-900/40 px-5 py-6 text-sm text-ink-400">
            No dApps connected yet.
          </div>
        ) : (
          <ul className="space-y-2">
            {connectedDapps.map((d) => (
              <li
                key={d.topic}
                className="rounded-xl border border-ink-800 bg-ink-900/40 p-4 flex items-center justify-between text-sm"
              >
                <div className="flex items-center gap-3">
                  {d.iconUrl && (
                    <img
                      src={d.iconUrl}
                      alt={d.name}
                      className="w-8 h-8 rounded"
                      referrerPolicy="no-referrer"
                    />
                  )}
                  <div>
                    <div className="text-ink-100 font-medium">{d.name}</div>
                    <div className="text-xs text-ink-400">{d.url}</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => void disconnect(d.topic)}
                  className="rounded-lg border border-ink-800 bg-ink-900/60 px-3 py-1.5 text-xs text-ink-100 hover:bg-ink-800/60 transition-colors"
                >
                  Disconnect
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
